import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { BookOpen } from 'lucide-react';
import type { Lesson, Difficulty } from '@/lib/learn';

const DIFFICULTY_STYLES: Record<Difficulty, string> = {
  beginner: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  intermediate: 'bg-amber-100 text-amber-800 border-amber-300',
  advanced: 'bg-purple-100 text-purple-700 border-purple-300',
};

interface LessonSidebarProps {
  lessons: Lesson[];
  currentSlug: string;
}

export const LessonSidebar: React.FC<LessonSidebarProps> = ({ lessons, currentSlug }) => {
  return (
    <nav className="sticky top-24">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-4 h-4 text-navy" />
        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">
          Learning Path
        </h3>
      </div>

      <ol className="space-y-1">
        {lessons.map((lesson) => {
          const isCurrent = lesson.slug === currentSlug;
          return (
            <li key={lesson.slug}>
              <Link
                to={`/intro-to-astrophotography/${lesson.slug}`}
                className={`flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isCurrent
                    ? 'bg-navy/5 text-foreground font-medium border border-navy/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
              >
                <span
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5 ${
                    isCurrent
                      ? 'bg-navy text-white'
                      : 'bg-secondary text-muted-foreground'
                  }`}
                >
                  {lesson.order}
                </span>
                <div className="min-w-0">
                  <span className="line-clamp-2 leading-snug">{lesson.title}</span>
                  <Badge
                    variant="outline"
                    className={`mt-1 text-[10px] px-1.5 py-0 capitalize ${DIFFICULTY_STYLES[lesson.difficulty]}`}
                  >
                    {lesson.difficulty}
                  </Badge>
                </div>
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
